"use client";

import { Calendar, User, Tag, ArrowLeft, Share2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-32 rounded-md bg-muted" />
          <div className="h-12 w-3/4 rounded-md bg-muted" />
          <div className="h-6 w-1/2 rounded-md bg-muted" />
          <div className="h-96 w-full rounded-lg bg-muted" />
          <div className="space-y-3">
            <div className="h-4 w-full rounded-md bg-muted" />
            <div className="h-4 w-full rounded-md bg-muted" />
            <div className="h-4 w-5/6 rounded-md bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <p className="font-default text-lg font-semibold text-foreground">
          Unable to load this article
        </p>
        <p className="mt-2 text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <p className="font-default text-lg font-semibold text-foreground">
          Article not found
        </p>
        <Link to="#" className="mt-4 inline-block text-primary underline-offset-4 hover:underline">
          Return home
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Elite Group Editorial";
  const authorAvatar =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"];

  return (
    <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      {/* Back button */}
      <div className="mb-8">
        <Link
          to="#"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to news
        </Link>
      </div>

      {/* Categories */}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={cat.link || "#"}
              data-index={i}
              className="inline-flex items-center rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-foreground transition-colors hover:bg-accent/80"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      {/* Post Title */}
      <h1
        className="font-default font-serif text-4xl font-semibold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {/* Post Meta */}
      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <span className="font-medium text-foreground">{authorName}</span>
        </div>
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
          </div>
        )}
      </div>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="mt-10 overflow-hidden rounded-lg shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {/* Excerpt */}
      {post.excerpt && (
        <div
          className="mt-10 border-l-4 border-primary pl-6 font-default text-xl italic leading-relaxed text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {/* Content */}
      <div
        className="prose prose-lg mt-10 max-w-none font-default text-base leading-relaxed text-foreground prose-headings:font-default prose-headings:font-serif prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-strong:text-foreground prose-blockquote:border-primary prose-blockquote:text-muted-foreground prose-img:rounded-lg"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Tag className="h-4 w-4 text-muted-foreground" />
          {post.tags.map((tag, i) => (
            <Link
              key={tag.id}
              to={tag.link || "#"}
              data-index={i}
              className="inline-flex items-center rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
            >
              #{tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      {/* Author Card */}
      <Card className="bg-card shadow-md">
        <CardContent className="flex flex-col items-start gap-6 p-8 sm:flex-row sm:items-center">
          <Avatar className="h-20 w-20 shrink-0">
            {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xl">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Author
            </p>
            <h3 className="font-default mt-1 font-serif text-2xl font-semibold text-foreground">
              {authorName}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Elite Group Corporation Limited provides comprehensive OEM/ODM manufacturing solutions for global brands, ensuring quality and compliance at every stage.
            </p>
            <div className="mt-4 flex gap-3">
              {post.author?.link && (
                <Link to={post.author.link}>
                  <Button variant="outline" size="sm">
                    <User className="h-4 w-4" />
                    View profile
                  </Button>
                </Link>
              )}
              <Button variant="ghost" size="sm">
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

export default function News() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="4491" id="single-post-template-section" className="bg-background">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}